// File: /pages/About/FAQs.js
import React, { useState, useEffect } from "react";
import Discuss from "parts/Discuss";
import Modal from "components/Modal";
import axios from "axios";

// Fallback data in case CMS API is unavailable
const FALLBACK_FAQ_DATA = [
  {
    id: "managed-it",
    title: "Understanding Managed IT Services",
    items: [
      "What Are Managed IT Services?",
      "Benefits of Managed IT for Businesses",
      "Difference Between Managed IT Services and Break-Fix",
      "How to Choose the Right MSP",
      "In-House IT vs. Outsourced IT",
    ],
  },
  {
    id: "cybersecurity",
    title: "Cybersecurity and Data Protection",
    items: [
      "Cybersecurity Threats Facing Small and Medium Businesses",
      "How Managed IT Services Enhance Cybersecurity",
      "Importance of Data Backup and Disaster Recovery",
      "Employee Cybersecurity Training",
      "Compliance and Regulatory Support",
    ],
  },
  {
    id: "cloud",
    title: "Cloud Services and Remote Work Solutions",
    items: [
      "What Are Cloud Services?",
      "Benefits of Cloud Storage and Collaboration Tools",
      "Security in the Cloud",
      "Transitioning to Cloud Services",
      "Remote Work IT Support",
    ],
  },
  {
    id: "support",
    title: "IT Support and Helpdesk Services",
    items: [
      "24/7 IT Support",
      "Helpdesk Support for Common IT Issues",
      "Onsite vs. Remote IT Support",
      "Proactive Monitoring and Maintenance",
      "Service Level Agreements (SLAs)",
    ],
  },
  {
    id: "network",
    title: "Network Management and Optimization",
    items: [
      "What Is Network Management?",
      "Benefits of Proactive Network Monitoring",
      "Optimizing Network Performance",
      "Wi-Fi and Connectivity Solutions",
      "Managing Network Security",
    ],
  },
  {
    id: "cost",
    title: "Cost Efficiency and Budget Management in IT",
    items: [
      "How Managed IT Services Help Control Costs",
      "Reducing Downtime with Proactive IT Management",
      "Optimizing Technology Investments",
      "Scaling IT Services as Your Business Grows",
      "Achieving Cost Transparency with Managed IT",
    ],
  },
];

export default function FAQs() {
  const [activeQuestion, setActiveQuestion] = useState(null);
  const [activeAnswer, setActiveAnswer] = useState("");
  const [cmsFaqs, setCmsFaqs] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch CMS FAQs on mount
  useEffect(() => {
    const fetchFaqs = async () => {
      try {
        const baseUrl = process.env.REACT_APP_API_URL ||
          (window.location.hostname === 'localhost' ? 'http://localhost:5000/api' : '/api');
        const res = await axios.get(`${baseUrl}/faqs`);
        if (res.data && res.data.length > 0) {
          setCmsFaqs(res.data);
        }
      } catch (err) {
        // Silently fall back to static data
      } finally {
        setLoading(false);
      }
    };
    fetchFaqs();
  }, []);

  // If CMS FAQs are loaded, group them by category and render
  if (cmsFaqs && cmsFaqs.length > 0) {
    // Group FAQs by category
    const grouped = cmsFaqs.reduce((acc, faq) => {
      const cat = faq.category || 'General';
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(faq);
      return acc;
    }, {});

    const categories = Object.keys(grouped);

    return (
      <>
        <section className="container mx-auto px-6 py-20 max-w-7xl">
          <h1 className="text-5xl font-bold text-theme-blue text-center mb-16">
            Frequently Asked Questions
          </h1>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-16 gap-y-16">
            {categories.map((category) => (
              <div key={category}>
                <h2 className="text-xl font-semibold mb-6 text-gray-900">
                  {category}
                </h2>
                <ul className="divide-y">
                  {grouped[category].map((faq) => (
                    <li key={faq.id}>
                      <button
                        type="button"
                        onClick={() => {
                          setActiveQuestion(faq.question);
                          setActiveAnswer(faq.answer);
                        }}
                        className="w-full flex justify-between items-center py-4 text-left text-theme-green hover:underline font-medium"
                      >
                        <span>{faq.question}</span>
                        <span className="text-gray-400 text-xl">›</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        <Modal
          open={Boolean(activeQuestion)}
          onClose={() => { setActiveQuestion(null); setActiveAnswer(""); }}
          title={activeQuestion || ""}
        >
          <p className="text-gray-600 leading-relaxed whitespace-pre-line">
            {activeAnswer || "Answer coming soon."}
          </p>
        </Modal>

        <Discuss />
      </>
    );
  }

  // Fallback to static FAQ data
  return (
    <>
      <section className="container mx-auto px-6 py-20 max-w-7xl">
        <h1 className="text-5xl font-bold text-theme-blue text-center mb-16">
          Frequently Asked Questions
        </h1>

        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-16 gap-y-16">
            {FALLBACK_FAQ_DATA.map((section) => (
              <div key={section.id}>
                <h2 className="text-xl font-semibold mb-6 text-gray-900">
                  {section.title}
                </h2>

                <ul className="divide-y">
                  {section.items.map((question) => (
                    <li key={question}>
                      <button
                        type="button"
                        onClick={() => {
                          setActiveQuestion(question);
                          setActiveAnswer("");
                        }}
                        className="w-full flex justify-between items-center py-4 text-left text-theme-green hover:underline font-medium"
                      >
                        <span>{question}</span>
                        <span className="text-gray-400 text-xl">›</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </section>

      <Modal
        open={Boolean(activeQuestion)}
        onClose={() => { setActiveQuestion(null); setActiveAnswer(""); }}
        title={activeQuestion || ""}
      >
        <p className="text-gray-600 leading-relaxed">
          {activeAnswer || (
            <>
              This is where the detailed answer for
              <strong> {activeQuestion}</strong> will go.
              <br /><br />
              You can later replace this with real content or fetch it dynamically
              from a CMS or API.
            </>
          )}
        </p>
      </Modal>

      <Discuss />
    </>
  );
}
